import React, { useMemo } from 'react';
import { Card } from 'tea-component';
import { getLocale } from 'umi';
import classNames from 'classnames';
import { getName } from '@home/utils/common';
import { dynamicString } from '@home/utils/dynamicString';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { resolveMainValueDisplay } from '../../utils/dataFormatter';
import './SimpleMetricGrid.scss';
import { LANGUAGE_EN } from '@home/_config/config';
import CommonBubble from '@home/components/CommonBubble';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import { getDataByKey } from '../TrendChartCo/_utils/common';

interface SubMetricItem {
  metricKey: string;
  enName: string;
  cnName: string;
  explainEn: string;
  explainCn: string;
  formatRule: string;
  preUnit?: string; // 前置单位（如 $）
  endUnit?: string; // 后置单位（如 %）
}

interface SimpleMetricItem {
  metricKey: string;
  enName: string;
  cnName: string;
  explainEn: string;
  explainCn: string;
  formatRule: string;
  mainValueFormatRule?: string;
  decimal?: number;
  backgroundColor?: string;
  subMetrics?: SubMetricItem[];
  preUnit?: string; // 前置单位（如 $）
  endUnit?: string; // 后置单位（如 %）
  dataKey?: string;
}

interface SimpleMetricGridProps {
  metrics: SimpleMetricItem[];
  className?: string;
  style?: React.CSSProperties;
  data?: any;
  metricsData?: any[];
  isChange?: boolean;
  changeHeight?: number;
  height?: number;
}

export default function SimpleMetricGrid(props: SimpleMetricGridProps) {
  const { metrics = [], className, style, data, metricsData = [], isChange, changeHeight, height } = props;
  const isEN = getLocale() === LANGUAGE_EN;

  // 检查是否有任何 metric 配置了 subMetrics
  const hasAnySubMetrics = useMemo(() => {
    return metrics.some((item) => item.subMetrics && item.subMetrics.length > 0);
  }, [metrics]);

  const metricItems = useMemo(() => {
    return metrics.map((item) => {
      const {
        metricKey,
        enName,
        cnName,
        explainEn,
        explainCn,
        formatRule: formatter,
        mainValueFormatRule,
        backgroundColor = '#f5f5f5',
        subMetrics = [],
        preUnit = '',
        endUnit = '',
        dataKey = ''
      } = item;

      const dataItem =  (dataKey? getDataByKey(dataKey || '', data) : metricsData)?.find((v: any) => v?.metrics_name === metricKey) || {};
      const { value } = dataItem;
      const name = getName(isEN, dynamicString(enName, data), dynamicString(cnName, data)) || '';
      const explain = getName(isEN, dynamicString(explainEn, data), dynamicString(explainCn, data)) || '';

      // 格式化主指标值
      const formattedValue = dataFormat({ value, formatRule: dynamicString(formatter, data) });
      const { mainDisplayValue, mainValueBubble: rawMainValueBubble } = resolveMainValueDisplay({
        value,
        data,
        baseFormattedValue: formattedValue,
        mainValueFormatRule,
      });
      // 判断是否为有效数据：排除 '-'、null、undefined、空字符串
      const isValidValue = value !== '-' && value !== null && value !== undefined && value !== '' && formattedValue !== '-';
      const displayValue = isValidValue
        ? `${preUnit}${mainDisplayValue}${endUnit}`
        : formattedValue;
      // 仅当有缩写气泡时拼接单位，避免无效气泡渲染（保留 0 / '' 等合法展示值）
      const mainValueBubble = (rawMainValueBubble === null || rawMainValueBubble === undefined)
        ? null
        : `${rawMainValueBubble}${endUnit}`;

      const subMetricsData = subMetrics.map((subItem) => {
        const subDataItem = (dataKey? getDataByKey(dataKey || '', data) : metricsData)?.find((v: any) => v?.metrics_name === subItem.metricKey) || {};
        const subName = getName(isEN, dynamicString(subItem.enName, data), dynamicString(subItem.cnName, data));
        const subExplain = getName(isEN, dynamicString(subItem.explainEn, data), dynamicString(subItem.explainCn, data));
        
        // 格式化子指标值
        const subFormattedValue = dataFormat({ value: subDataItem.value, formatRule: dynamicString(subItem.formatRule, data) });
        const subPreUnit = subItem.preUnit || '';
        const subEndUnit = subItem.endUnit || '';
        // 判断是否为有效数据：排除 '-'、null、undefined、空字符串
        const isValidSubValue = subDataItem.value !== '-' && subDataItem.value !== null && subDataItem.value !== undefined && subDataItem.value !== '' && subFormattedValue !== '-';
        const subDisplayValue = isValidSubValue
          ? `${subPreUnit}${subFormattedValue}${subEndUnit}`
          : subFormattedValue;
        
        return {
          metricKey: subItem.metricKey,
          name: subName,
          value: subDisplayValue,
          explain: subExplain,
        };
      });

      return {
        metricKey,
        name,
        value: displayValue,
        mainValueBubble,
        explain,
        backgroundColor,
        subMetrics: subMetricsData,
      };
    });
  }, [metrics, metricsData, isEN, data]);

  return (
    <Card className={classNames('dbd-simple-metric-grid', className)} style={style}>
      <Card.Body>
        <div className="dbd-simple-metric-grid__container" style={{height: isChange? changeHeight || 117 : height || 120}}>
          {metricItems.map((item) => (
            <div
              key={item.metricKey}
              className={classNames('dbd-simple-metric-grid__item', {
                'dbd-simple-metric-grid__item--has-placeholder': hasAnySubMetrics && (!item.subMetrics || item.subMetrics.length === 0),
              })}
              style={{ backgroundColor: item.backgroundColor }}
            >
              <CommonBubble content={item.explain && <FormatHtmlTextToReact text={item.explain} />}>
                <div className="dbd-simple-metric-grid__item-title">{item.name}</div>
              </CommonBubble>
              <CommonBubble dark placement="bottom" content={item.mainValueBubble}>
                <div className="dbd-simple-metric-grid__item-value">{item.value}</div>
              </CommonBubble>
              {item.subMetrics && item.subMetrics.length > 0 && (
                <div className="dbd-simple-metric-grid__sub-metrics">
                  {item.subMetrics.map((subItem) => (
                    <div key={subItem.metricKey} className="dbd-simple-metric-grid__sub-metric-item">
                      <CommonBubble content={subItem.explain && <FormatHtmlTextToReact text={subItem.explain} />}>
                        <span className="dbd-simple-metric-grid__sub-metric-name">{subItem.name}{subItem.value}</span>
                      </CommonBubble>
                    </div>
                  ))}
                </div>
              )}
              {/* 智能占位：只有当全局有 subMetrics 但当前 item 没有时，才添加占位符 */}
              {hasAnySubMetrics && (!item.subMetrics || item.subMetrics.length === 0) && (
                <div className="dbd-simple-metric-grid__sub-metrics-placeholder" />
              )}
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}
